import React, {useEffect, useRef} from 'react';
import {View, StyleSheet, Animated, Easing} from 'react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {useCommonStyles} from "@/hooks/useCommonStyles";

interface SkeletonCardProps {
    hasImage?: boolean;
    delay?: number;
}

const SkeletonCard: React.FC<SkeletonCardProps> = ({hasImage = true, delay = 0}) => {
    const {theme} = useCurrentTheme();
    const commonStyles = useCommonStyles();
    const styles = createStyles(theme);
    const shimmerAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const shimmer = () => {
            shimmerAnim.setValue(0);
            Animated.timing(shimmerAnim, {
                toValue: 1,
                duration: 1500,
                easing: Easing.linear,
                useNativeDriver: true,
            }).start(() => shimmer());
        };

        const timer = setTimeout(() => {
            shimmer();
        }, delay);

        return () => clearTimeout(timer);
    }, [shimmerAnim, delay]);

    const shimmerTranslate = shimmerAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [-100, 100],
    });

    const ShimmerOverlay = () => (
        <Animated.View
            style={[
                styles.shimmerOverlay,
                commonStyles.absoluteOverlay,
                {
                    transform: [{translateX: shimmerTranslate}],
                },
            ]}
        />
    );

    return (
        <View style={[styles.skeletonCard, commonStyles.shadows.md]}>
            {hasImage && (
                <View style={styles.skeletonImageContainer}>
                    <View style={styles.skeletonImage}>
                        <ShimmerOverlay/>
                    </View>
                </View>
            )}

            <View style={styles.skeletonContent}>
                {/* Title skeleton */}
                <View style={styles.skeletonTitle}>
                    <ShimmerOverlay/>
                </View>

                {/* Text lines skeleton */}
                <View style={styles.skeletonTextContainer}>
                    <View style={[styles.skeletonText, {width: '90%'}]}>
                        <ShimmerOverlay/>
                    </View>
                    <View style={[styles.skeletonText, {width: '75%'}]}>
                        <ShimmerOverlay/>
                    </View>
                    <View style={[styles.skeletonText, {width: '60%'}]}>
                        <ShimmerOverlay/>
                    </View>
                </View>

                {/* Action area skeleton */}
                <View style={styles.skeletonActionContainer}>
                    <View style={styles.skeletonButton}>
                        <ShimmerOverlay/>
                    </View>
                    <View style={styles.skeletonBadge}>
                        <ShimmerOverlay/>
                    </View>
                </View>
            </View>
        </View>
    );
};

export const AppLoadingScreen: React.FC = () => {
    const {theme} = useCurrentTheme();
    const commonStyles = useCommonStyles();
    const styles = createStyles(theme);

    return (
        <View style={commonStyles.screenContainer}>
            <View style={styles.header}>
                {/* Header skeleton */}
                <View style={styles.skeletonHeaderTitle}/>
                <View style={styles.skeletonHeaderSubtitle}/>
            </View>

            <View style={styles.cardContainer}>
                {/* Featured card with image */}
                <SkeletonCard hasImage={true} delay={0}/>

                {/* Regular cards */}
                <SkeletonCard hasImage={false} delay={200}/>
                <SkeletonCard hasImage={false} delay={400}/>

                {/* Another card with image */}
                <SkeletonCard hasImage={true} delay={600}/>
            </View>

            {/* Bottom navigation skeleton */}
            <View style={styles.bottomNavSkeleton}>
                {[1, 2, 3, 4].map((_, index) => (
                    <View key={index} style={styles.navItemSkeleton}/>
                ))}
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    header: {
        paddingHorizontal: theme.spacing.lg,
        paddingTop: theme.spacing.xl,
        paddingBottom: theme.spacing.md,
    },
    skeletonHeaderTitle: {
        height: 32,
        width: '60%',
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.sm,
        marginBottom: theme.spacing.sm,
    },
    skeletonHeaderSubtitle: {
        height: 16,
        width: '40%',
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.sm,
    },
    cardContainer: {
        flex: 1,
        paddingHorizontal: theme.spacing.lg,
        gap: theme.spacing.md,
    },
    skeletonCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        overflow: 'hidden',
    },
    skeletonImageContainer: {
        height: 200,
        overflow: 'hidden',
    },
    skeletonImage: {
        width: '100%',
        height: '100%',
        backgroundColor: theme.colors.border,
        position: 'relative',
        overflow: 'hidden',
    },
    skeletonContent: {
        padding: theme.spacing.md,
    },
    skeletonTitle: {
        height: 24,
        width: '70%',
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.sm,
        marginBottom: theme.spacing.md,
        position: 'relative',
        overflow: 'hidden',
    },
    skeletonTextContainer: {
        marginBottom: theme.spacing.md,
        gap: theme.spacing.xs,
    },
    skeletonText: {
        height: 16,
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.sm,
        position: 'relative',
        overflow: 'hidden',
    },
    skeletonActionContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    skeletonButton: {
        height: 36,
        width: 100,
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.md,
        position: 'relative',
        overflow: 'hidden',
    },
    skeletonBadge: {
        height: 24,
        width: 60,
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.lg,
        position: 'relative',
        overflow: 'hidden',
    },
    shimmerOverlay: {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        width: 100,
    },
    bottomNavSkeleton: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    navItemSkeleton: {
        height: 32,
        width: 32,
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.md,
    },
});
